import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, UserCircle } from "@phosphor-icons/react";
import { api, extractApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";

export default function Profile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (user) setName(user.name || "");
  }, [user]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setErr("Callsign can't be blank."); return; }
    if (trimmed === user?.name) { setErr("That's already your callsign."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.patch("/auth/me", { name: trimmed });
      setUser((u) => ({ ...(u || {}), ...data }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e2) {
      setErr(extractApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-volt text-xs uppercase tracking-widest inline-flex items-center gap-1 mb-4" data-testid="profile-back">
        <ArrowLeft size={12} weight="bold" /> Back
      </button>

      <div className="flex items-center gap-3 mb-8">
        <UserCircle size={36} weight="bold" className="text-volt" />
        <div>
          <div className="label-mono text-muted-foreground">// OPERATOR</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black leading-none">Profile</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
        <form onSubmit={onSubmit} className="tactical-card p-6" data-testid="callsign-form">
          <div className="label-mono text-volt mb-3">// CALLSIGN</div>
          <p className="text-xs text-muted-foreground mb-4">
            How you appear to squadmates and on public plans you publish.
          </p>
          <label className="block text-xs label-mono mb-2 text-muted-foreground" htmlFor="callsign-input">Display Name</label>
          <input
            id="callsign-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Operator"
            maxLength={80}
            className="w-full bg-background border border-border px-3 py-2 outline-none focus:border-volt mb-4"
            data-testid="callsign-input"
          />
          {err && <div className="text-red-400 text-xs mb-3" data-testid="callsign-error">{err}</div>}
          {saved && <div className="text-volt text-xs mb-3 inline-flex items-center gap-1" data-testid="callsign-saved"><CheckCircle size={12} weight="fill" /> Saved.</div>}
          <button
            type="submit"
            disabled={busy}
            className="w-full bg-volt text-black py-2 font-display uppercase tracking-widest text-sm hover:bg-white disabled:opacity-50"
            data-testid="callsign-save-btn"
          >
            {busy ? "Saving..." : "Update Callsign"}
          </button>
        </form>

        <div className="tactical-card p-6">
          <div className="label-mono text-volt mb-3">// ACCOUNT</div>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Email</dt>
              <dd className="font-mono truncate" data-testid="profile-email">{user?.email || "—"}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Role</dt>
              <dd className="font-mono uppercase">{user?.role || "user"}</dd>
            </div>
          </dl>
          <p className="text-xs text-muted-foreground mt-6 leading-relaxed">
            Need to change your password? Use the <a href="/forgot-password" className="text-volt underline">forgot-password</a> flow — a fresh reset link will be emailed to you.
          </p>
        </div>
      </div>
    </Layout>
  );
}
